import { Component, OnInit, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { forkJoin, Observable, of } from 'rxjs';
import { map, catchError, takeUntil } from 'rxjs/operators';
import { BidConfigItem, MatchingAlgorithm } from 'src/app/interfaces/responses/models';
import { UtilityService } from 'src/app/services/utility.service';
import { ToastService } from 'src/app/services/toast.service';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmDialogComponent } from 'src/app/shared/confirm-dialog/confirm-dialog.component';
import { formatToApiDate, toBoolean, stringifyBooleans } from 'src/app/shared/utils/form-utils';
import { BaseComponent } from 'src/app/shared/base.component';

interface BiddingConfig {
  matchingAlgorithm: string;
  priceType: 'dynamic' | 'fixed';
  minPrice?: number;
  maxPrice?: number;
  fixedPrice?: number;
  hoursBeforeClosing?: number;
  maxRangeDays?: number;
  daysAhead?: number;
  enableFixPrice?: boolean;
  preferential_bid_mode?:string;
  fix_price_residential?:number;
  fix_price_commercial?:number;
}

type BidTypeKey = 'dayAhead' | 'monthly' | 'preferential';

const BID_TYPE_MAP: Record<BidTypeKey, number> = {
  monthly: 1,
  dayAhead: 2,
  preferential: 3,
};

const BID_TYPE_LABEL: Record<BidTypeKey, string> = {
  monthly: 'Monthly',
  dayAhead: 'Day Ahead',
  preferential: 'Preferential',
};

@Component({
  selector: 'app-bidding-type',
  templateUrl: './bidding-type.component.html',
  styleUrls: ['./bidding-type.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class BiddingTypeComponent extends BaseComponent implements OnInit {

  utilityId = '';
  matchingAlgorithms: MatchingAlgorithm[] = [];
  bidConfigItems: BidConfigItem[] = [];
  utilityConfig: any = null;

  showDialog = false;
  isLoadingBiddingConfig = true;
  isSavingBiddingConfig = false;
  selectedBidType: BidTypeKey | null = null;
  currentConfig: BiddingConfig = { matchingAlgorithm: '', priceType: 'dynamic' };

  enabledStatus: Record<BidTypeKey, boolean> = {
    dayAhead: false,
    monthly: false,
    preferential: false,
  };

  configs: Record<BidTypeKey, BiddingConfig> = {
    dayAhead: {
      matchingAlgorithm: '',
      priceType: 'dynamic',
      minPrice: 5,
      maxPrice: 2500,
      fixedPrice: 24,
      hoursBeforeClosing: 8,
    },
    monthly: {
      matchingAlgorithm: '',
      priceType: 'dynamic',
      minPrice: 10,
      maxPrice: 20,
      fixedPrice: 6,
      hoursBeforeClosing: 6,
    },
    preferential: {
      matchingAlgorithm: '',
      priceType: 'fixed',
      minPrice: 5,
      maxPrice: 20,
      fixedPrice: 6,
      maxRangeDays: 10,
      daysAhead: 2,
      enableFixPrice: false,
      preferential_bid_mode:"fixed",
      fix_price_residential:3,
      fix_price_commercial:4,
    },
  };

  private initialConfigs: Record<BidTypeKey, BiddingConfig | null> = {
    dayAhead: null,
    monthly: null,
    preferential: null,
  };

  constructor(
    private utilityService: UtilityService,
    private route: ActivatedRoute,
    private dialog: MatDialog,
    private toastService: ToastService,
    private cdr: ChangeDetectorRef
  ) {
    super();
  }

  ngOnInit(): void {
    this.utilityId = this.route.snapshot.paramMap.get('id') ?? '';
    this.initData();
  }

  private initData(): void {
    this.isLoadingBiddingConfig = true;
    forkJoin([
      this.utilityService.getMatchingAlgorithm(),
      this.fetchBidConfig(),
      this.fetchUtilityConfig()
    ]).pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (results: any[]) => {
          const [algos, bidItems, utilityData] = results;
          this.matchingAlgorithms = algos.items || [];
          this.applyDefaultAlgorithm(); 
          this.applyBidConfig(bidItems);
          this.applyUtilityConfig(utilityData); 
          this.isLoadingBiddingConfig = false;
          this.captureInitialState();
          this.cdr.markForCheck();
        },
        error: (err) => {
          console.error('[BiddingType] Initialization failed:', err);
          this.isLoadingBiddingConfig = false;
          this.toastService.show('Failed to load initial data.', 'error');
          this.cdr.markForCheck();
        }
      });
  }

  private fetchBidConfig(): Observable<BidConfigItem[]> {
    return this.utilityService.getUtilityBidConfiguration(this.utilityId).pipe(
      map(res => res.items || []),
      catchError(err => {
        console.error('[BiddingType] Failed to fetch bid config:', err);
        return of([]);
      })
    );
  }

  private fetchUtilityConfig(): Observable<any> {
    return this.utilityService.getUtilityConfiguration(this.utilityId).pipe(
      catchError(err => {
        console.error('[BiddingType] Failed to fetch utility config:', err);
        return of(null);
      })
    );
  }

  private applyBidConfig(items: BidConfigItem[]): void {
    this.bidConfigItems = items;
    items.forEach(item => {
      const type = this.resolveTypeKey(item.bid_type);
      if (type) this.populateConfig(type, item);
    });
  }

  private applyUtilityConfig(data: any): void {
    if (!data || !data.config) return;
    this.utilityConfig = data;
    const cfg = data.config;

    this.enabledStatus.monthly = toBoolean(cfg.enable_monthly_bid);
    this.enabledStatus.dayAhead = toBoolean(cfg.enable_day_ahead_bid);
    this.enabledStatus.preferential = toBoolean(cfg.enable_preferential_bid);

    const pref = this.configs.preferential;
    pref.maxRangeDays = cfg.maximum_range_in_days ?? pref.maxRangeDays;
    pref.fix_price_commercial = cfg.fix_price_commercial ?? pref.fix_price_commercial;
    pref.fix_price_residential = cfg.fix_price_residential ?? pref.fix_price_residential;
    pref.preferential_bid_mode = cfg.preferential_bid_mode ?? pref.preferential_bid_mode;
    pref.daysAhead = cfg.day_ahead_in_days ?? pref.daysAhead;
    pref.hoursBeforeClosing = this.extractNum(cfg.hours_left_to_close) ?? pref.hoursBeforeClosing;

    const isFixRate = toBoolean(cfg.enable_fix_price);
    pref.enableFixPrice = isFixRate;
    pref.priceType = isFixRate ? 'fixed' : 'dynamic';
    pref.fixedPrice = this.extractNum(cfg.fix_price) ?? pref.fixedPrice;
    pref.minPrice = this.extractNum(cfg.min_price) ?? pref.minPrice;
    pref.maxPrice = this.extractNum(cfg.max_price) ?? pref.maxPrice;
  }

  private resolveTypeKey(bidTypeId: any): BidTypeKey | null {
    const entry = (Object.entries(BID_TYPE_MAP) as [BidTypeKey, number][])
      .find(([, id]) => id == bidTypeId); 
    return entry ? entry[0] : null;
  }

  private populateConfig(type: BidTypeKey, item: BidConfigItem): void {
    if (!item) return;
    const cfg = this.configs[type];
    const rawConfig = (item as any).config || {};

    const hours = this.extractNum(item.hours_left_to_close ?? rawConfig.hours_left_to_close);
    if (hours !== null) cfg.hoursBeforeClosing = hours;

    const algo = item.matching_algorithm || rawConfig.matching_algorithm || '';
    if (algo) cfg.matchingAlgorithm = this.findAlgorithmId(algo);

    const retail = this.extractNum(item.retail_price ?? rawConfig.retail_price);
    const wholesale = this.extractNum(item.wholesale_price ?? rawConfig.wholesale_price);

    if (type !== 'preferential') {
      if (retail !== null && wholesale !== null) {
        if (retail === wholesale) {
          cfg.priceType = 'fixed';
          cfg.fixedPrice = retail;
        } else {
          cfg.priceType = 'dynamic';
          cfg.minPrice = wholesale;
          cfg.maxPrice = retail;
        }
      }
    }
  }

  private extractNum(val: any): number | null {
    if (val == null || val === '') return null;
    const parsed = Number(val);
    return isNaN(parsed) ? null : parsed;
  }

  private applyDefaultAlgorithm(): void {
    if (!this.matchingAlgorithms.length) return;
    const defaultId = this.matchingAlgorithms[0].id;
    (Object.keys(this.configs) as BidTypeKey[]).forEach((key) => {
      if (!this.configs[key].matchingAlgorithm) {
        this.configs[key].matchingAlgorithm = defaultId;
      }
    });
  }

  private findAlgorithmId(keyOrId: string): string {
    if (!keyOrId) return '';
    const found = this.matchingAlgorithms.find(
      (a) => a.algorithm_key === keyOrId || a.id === keyOrId,
    );
    return found ? found.id : keyOrId;
  }

  openDialog(type: BidTypeKey): void {
    if (!this.enabledStatus[type]) return;
    this.selectedBidType = type;
    this.currentConfig = { ...this.configs[type] };
    this.showDialog = true;
    this.cdr.markForCheck();
  }

  onToggleChange(type: BidTypeKey, event: Event): void {
    const checked = (event.target as HTMLInputElement).checked;
    this.enabledStatus[type] = checked;

    const payload = this.buildUtilityPayload();
    this.utilityService.updateUtilityConfig(this.utilityId, payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.utilityConfig = res;
          this.syncEnabledStatus(res?.config);
          this.toastService.show(`${BID_TYPE_LABEL[type]} bid status updated!`);
          this.cdr.markForCheck();
        },
        error: (err) => {
          console.error('[BiddingType] Update failed:', err);
          this.toastService.show('Failed to update status.', 'error');
          this.initData();
        }
      });
  }

  discardChanges(): void {
    if (this.isConfigDirty()) {
      const ref = this.dialog.open(ConfirmDialogComponent, {
        panelClass: 'custom-dialog-container',
        data: { title: 'Confirmation', message: 'Discard unsaved changes?' },
      });
      ref.afterClosed().pipe(takeUntil(this.destroy$)).subscribe((confirmed) => { if (confirmed) this.closeDialog(); });
    } else {
      this.closeDialog();
    }
  }

  closeDialog(): void {
    this.showDialog = false;
    this.isSavingBiddingConfig = false;
    this.selectedBidType = null;
    this.cdr.markForCheck();
  }

  public saveConfiguration(): void {
    if (!this.selectedBidType) return;
    const type = this.selectedBidType;
    this.configs[type] = { ...this.currentConfig };

    const tasks: Observable<any>[] = [];
    if (type !== 'preferential') {
      tasks.push(this.updateBidConfiguration(type));
    }

    const extra: any = {};
    if (type === 'preferential') {
      extra.maximum_range_in_days = this.currentConfig.maxRangeDays ?? 0;
      extra.day_ahead_in_days = this.currentConfig.daysAhead ?? 0;
      extra.hours_left_to_close = this.currentConfig.hoursBeforeClosing ?? 0;
      extra.preferential_bid_mode = this.currentConfig.preferential_bid_mode ?? 0;
      extra.fix_price_commercial = this.currentConfig.fix_price_commercial ?? 0;
      extra.fix_price_residential = this.currentConfig.fix_price_residential ?? 0;  

      const isFixed = this.currentConfig.priceType === 'fixed';
      extra.enable_fix_price = isFixed;
      extra.fix_price = this.currentConfig.fixedPrice ?? 0;
      extra.min_price = this.currentConfig.minPrice ?? 0;
      extra.max_price = this.currentConfig.maxPrice ?? 0;
    }

    this.isSavingBiddingConfig = true;
    tasks.push(this.utilityService.updateUtilityConfig(this.utilityId, this.buildUtilityPayload(extra)));

    forkJoin(tasks).pipe(takeUntil(this.destroy$)).subscribe({
      next: () => {
        this.isSavingBiddingConfig = false;
        this.toastService.show(`${BID_TYPE_LABEL[type]} configuration saved!`);
        this.initData();
        this.closeDialog();
      },
      error: (err) => {
        this.isSavingBiddingConfig = false;
        console.error(`[BiddingType] Save failed:`, err);
        this.toastService.show('Failed to save configuration.', 'error');
        this.cdr.markForCheck();
      }
    });
  }

  private buildUtilityPayload(overrides: any = {}): any {
    const existingConfig = this.utilityConfig?.config || {};
    const allowedKeys = [
      'enable_monthly_bid', 'enable_day_ahead_bid', 'enable_preferential_bid',
      'allow_prosumers_to_sell', 'allow_prosumers_to_buy',
      'allow_consumers_to_sell', 'allow_consumers_to_buy',
      'allow_utility_to_sell', 'allow_utility_to_buy',
      'enable_fix_price', 'fix_price', 'min_price', 'max_price',
      'maximum_range_in_days', 'day_ahead_in_days', 'hours_left_to_close',
      'fix_price_commercial', 'fix_price_residential', 'preferential_bid_mode'
    ];

    const safeExisting: any = {};
    allowedKeys.forEach(key => {
      if (existingConfig[key] !== undefined) safeExisting[key] = existingConfig[key];
    });

    const configData: any = stringifyBooleans({
      ...safeExisting,
      enable_monthly_bid: this.enabledStatus.monthly,
      enable_day_ahead_bid: this.enabledStatus.dayAhead,
      enable_preferential_bid: this.enabledStatus.preferential,
    });

    Object.assign(configData, overrides);

    return {
      config: configData,
      validity_start: formatToApiDate(),
      validity_end: '2099-12-31 23:59:59',
    };
  }

  private updateBidConfiguration(type: BidTypeKey): Observable<any> {
    const bidTypeId = BID_TYPE_MAP[type];
    const cfg = this.configs[type];
    const payload = {
      config: {
        wholesale_price: cfg.priceType === 'dynamic' ? (cfg.minPrice ?? 0) : (cfg.fixedPrice ?? 0),
        retail_price: cfg.priceType === 'dynamic' ? (cfg.maxPrice ?? 0) : (cfg.fixedPrice ?? 0),
        hours_left_to_close: cfg.hoursBeforeClosing ?? 0,
        matching_algorithm: cfg.matchingAlgorithm
      }
    };
    return this.utilityService.updateBidConfig(this.utilityId, bidTypeId, payload);
  }

  private syncEnabledStatus(cfg: any): void {
    if (!cfg) return;
    this.enabledStatus.monthly = toBoolean(cfg.enable_monthly_bid);
    this.enabledStatus.dayAhead = toBoolean(cfg.enable_day_ahead_bid);
    this.enabledStatus.preferential = toBoolean(cfg.enable_preferential_bid);
  }

  private captureInitialState(): void {
    (Object.keys(this.configs) as BidTypeKey[]).forEach((key) => {
      this.initialConfigs[key] = JSON.parse(JSON.stringify(this.configs[key]));
    });
  }

  private isConfigDirty(): boolean {
    if (!this.selectedBidType) return false;
    return JSON.stringify(this.currentConfig) !== JSON.stringify(this.initialConfigs[this.selectedBidType]);
  }
}
